import { useState, type ChangeEvent, type CSSProperties, type Ref } from 'react';

/**
 * A file picker with English, brand-consistent chrome.
 *
 * The native `<input type="file">` renders its label in the browser's locale
 * ("Bestand kiezen / Geen bestand gekozen" on a Dutch browser), which broke the
 * English-only UI and looked nothing like our buttons. This hides the native
 * input and shows our own label — "Choose file", then the selected filename —
 * while forwarding `ref`, `onChange` and `accept` so it drops in wherever a file
 * input was, including forms that read the file from a ref at submit time.
 */
export function FileButton({
  accept,
  multiple,
  onChange,
  inputRef,
  label = 'Choose file',
  className = 'btn sm',
  style,
  disabled,
}: {
  accept?: string;
  multiple?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  inputRef?: Ref<HTMLInputElement>;
  label?: string;
  className?: string;
  style?: CSSProperties;
  disabled?: boolean;
}) {
  const [name, setName] = useState<string | null>(null);
  return (
    <label
      className={className}
      title={name ?? undefined}
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        maxWidth: 280,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'inline-block',
        opacity: disabled ? 0.5 : 1,
        ...style,
      }}
    >
      {name ?? label}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        hidden
        onChange={(e) => {
          const files = e.target.files;
          setName(files && files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? null);
          onChange?.(e);
        }}
      />
    </label>
  );
}
